import { X } from "lucide-react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useBackLayer } from "../../lib/backlayer.ts";
import { allowZoom } from "../../lib/session.ts";
import { ICON } from "../../ui/index.ts";

/* Hallmark · component: modal (full-screen viewer) · genre: playful · theme: project palette
 * pre-emit critique: P5 H5 E4 S5 R5 V4
 * states: default · hover · focus · active · disabled · loading · error · success
 * contrast: pass (white on near-black) · mobile: pass at 320/375/414/768 · tokens: pass
 */

/**
 * The recording at the size the screen can give it.
 *
 * The sheet shows a 3:4 frame, which is a shape rather than the picture — so this is where the
 * picture itself lives: a photo whole, a video playing, and nothing else on screen but the way
 * out. Dark, because once you are looking at the thing itself there is nothing left to read.
 *
 * It is also the video's only player, and it covers the sheet while it is open — so rather than
 * the sheet reading the clock through it, closing it hands back where the clip was left. That is
 * what a timed comment and the highlight form fill themselves in from.
 *
 * The clip plays here with the browser's own controls — a scrubber, a clock, the speed and the
 * volume where everybody already knows to look. The card behind has the gestures instead: that
 * is the one to flick through, and this is the one to find a particular second in.
 */

export function MediaViewer({
  assetId,
  src,
  isVideo,
  startAtMs,
  label,
  onClose,
}: {
  assetId: string;
  src: string;
  isVideo: boolean;
  /** Open it at a marked moment, when that is what was tapped. */
  startAtMs?: number | null;
  /** What the recording is, for whoever cannot see it. */
  label: string;
  /** Closing hands back where the clip was left, in ms, for whatever wants to mark it. */
  onClose: (atMs: number | null) => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const media = useRef<HTMLVideoElement>(null);
  /** Where a press landed, so a drag across the black is not read as a tap on it. */
  const from = useRef<{ x: number; y: number } | null>(null);
  const leave = () => {
    const t = media.current?.currentTime;
    onClose(t === undefined || !Number.isFinite(t) ? null : Math.round(t * 1000));
  };
  // Back closes the picture and puts the reader back on the card it came from, rather than
  // walking out of the library altogether.
  useBackLayer(leave);
  useEffect(() => {
    closeRef.current?.focus();
  }, []);
  // The one place in the app that scales. Nothing else is worth getting closer to, and this
  // covers the screen by itself, so a pinch here magnifies the picture rather than the
  // interface around it. Closing clamps the viewport again, which is also what undoes the zoom.
  useEffect(() => {
    allowZoom(true);
    return () => allowZoom(false);
  }, []);
  // The way out keeps its place and its size, whatever the picture does. A page zoom scales
  // everything on screen and then slides the lot about as the reader pans, so the ✕ would swell,
  // drift and leave with the rest of the page — on the one screen whose whole purpose is looking
  // closer. It is pinned to the visual viewport instead, which is the part of the page actually
  // on screen: placed against that corner, and scaled back down by as much as the page was
  // scaled up. At rest the sums come to nothing and it sits exactly where the stylesheet put it.
  useEffect(() => {
    const vv = window.visualViewport;
    const el = closeRef.current;
    if (!vv || !el) return;
    const home = el.getBoundingClientRect();
    const fromTop = home.top;
    const fromRight = window.innerWidth - home.right;
    el.style.transformOrigin = "top left";
    const place = () => {
      const x = vv.offsetLeft + vv.width - (fromRight + home.width) / vv.scale;
      const y = vv.offsetTop + fromTop / vv.scale;
      el.style.transform = `translate(${x - home.left}px, ${y - home.top}px) scale(${1 / vv.scale})`;
    };
    place();
    vv.addEventListener("resize", place);
    vv.addEventListener("scroll", place);
    return () => {
      vv.removeEventListener("resize", place);
      vv.removeEventListener("scroll", place);
    };
  }, []);
  // And the pager underneath is frozen while it is up. Which element a two-finger drag ends up
  // scrolling is the browser's business, not ours, and on the phone it was finding the track
  // through a screen that covers it: the recording behind changed while a photograph was being
  // looked at. A scroller that cannot scroll cannot be found by any gesture.
  useEffect(() => {
    document.body.classList.add("viewing");
    return () => document.body.classList.remove("viewing");
  }, []);

  return createPortal(
    <div
      className="viewer"
      role="dialog"
      aria-modal="true"
      aria-label={`${label}, full size`}
      // The sheet is underneath and closes on a backdrop click of its own, so both the click and
      // the key stop here: leaving full screen must never also throw the recording away.
      onPointerDown={(e) => {
        from.current = { x: e.clientX, y: e.clientY };
      }}
      onClick={(e) => {
        e.stopPropagation();
        const start = from.current;
        from.current = null;
        // A tap on the black puts the picture away. A drag across it is somebody moving a
        // zoomed photograph, or a swipe that missed, and either way it is not a dismissal.
        if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > 10) return;
        leave();
      }}
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        e.stopPropagation();
        leave();
      }}
    >
      <button
        ref={closeRef}
        type="button"
        className="viewer-close"
        onClick={leave}
        aria-label="Close full size"
      >
        <X {...ICON} aria-hidden="true" />
      </button>

      {/* The media keeps its own clicks: tapping a video is how it is paused, and dismissing the
          whole screen on that would be a trap. */}
      {isVideo ? (
        <video
          ref={media}
          id={`player-${assetId}`}
          className="viewer-media"
          src={src}
          controls
          autoPlay
          playsInline
          onClick={(e) => e.stopPropagation()}
          onLoadedMetadata={(e) => {
            if (startAtMs != null) e.currentTarget.currentTime = startAtMs / 1000;
          }}
        />
      ) : (
        <img className="viewer-media" src={src} alt={label} onClick={(e) => e.stopPropagation()} />
      )}
    </div>,
    document.body,
  );
}
